import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { defer, finalize, tap } from 'rxjs';

import { DatabaseBrowserService } from '../../../core/database-browser/database-browser.service';
import {
  DatabaseImportResult,
  DatabaseRowsResponse,
  DatabaseTableSummary,
} from '../../../core/database-browser/database-browser.types';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog';
import { IconComponent } from '../../../shared/icon/icon';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

const PAGE_SIZE = 50;
const FALLBACK_EXPORT_NAME = 'flow-db-export.zip';

/** Pull the filename out of `attachment; filename="..."`, if the header is readable. */
function filenameFromDisposition(header: string | null): string {
  const match = /filename="?([^";]+)"?/.exec(header ?? '');
  return match?.[1] ?? FALLBACK_EXPORT_NAME;
}

/**
 * Read-only admin database browser.
 *
 * Left rail: a searchable list of tables (one per Django model) from
 * `GET /api/admin/database/tables/`. Selecting one loads its rows from
 * `.../rows/`, which supports a free-text search and click-to-sort columns.
 * All state lives in signals; every table/search/sort/page change re-fetches.
 *
 * The page also carries the whole-database export / import actions: export
 * downloads a zip from `.../export/`; import posts one back to `.../import/`,
 * which replaces every table and is therefore confirmed as destructive first.
 */
@Component({
  selector: 'app-database-browser',
  imports: [
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    IconComponent,
    DashPipe,
  ],
  templateUrl: './database-browser.html',
  styleUrl: './database-browser.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatabaseBrowserComponent implements OnInit {
  private readonly service = inject(DatabaseBrowserService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  // ── Export / import ───────────────────────────────────────────────────────
  protected readonly exporting = signal(false);
  protected readonly exportError = signal<string | null>(null);
  protected readonly importing = signal(false);
  protected readonly importFile = signal<File | null>(null);
  // A refused environment or a bad archive is a 200 with `status: 'error'`, so
  // it renders here rather than as a failure inside the dialog.
  protected readonly importResult = signal<DatabaseImportResult | null>(null);

  // ── Table list (left rail) ────────────────────────────────────────────────
  protected readonly tables = signal<DatabaseTableSummary[]>([]);
  protected readonly tablesLoading = signal(true);
  protected readonly tablesError = signal(false);
  protected readonly tableFilter = signal('');

  // ── Selected table + its rows ─────────────────────────────────────────────
  protected readonly selectedKey = signal<string | null>(null);
  protected readonly rows = signal<DatabaseRowsResponse | null>(null);
  protected readonly rowsLoading = signal(false);
  protected readonly rowsError = signal(false);

  private readonly page = signal(1);
  private readonly search = signal('');
  // e.g. "created_at" / "-created_at"; null means server default ordering.
  private readonly orderBy = signal<string | null>(null);

  ngOnInit(): void {
    this.loadTables();
  }

  protected loadTables(): void {
    this.tablesLoading.set(true);
    this.tablesError.set(false);
    this.cdr.markForCheck();

    this.service
      .getTables()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tables.set(res.tables);
          this.tablesLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.tablesLoading.set(false);
          this.tablesError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  /** Tables filtered by the left-rail filter box (matches key or table name). */
  protected readonly filteredTables = computed<DatabaseTableSummary[]>(() => {
    const term = this.tableFilter().trim().toLowerCase();
    const all = this.tables();
    if (!term) {
      return all;
    }
    return all.filter(
      (t) =>
        t.key.toLowerCase().includes(term) ||
        t.table.toLowerCase().includes(term) ||
        t.verbose_name.toLowerCase().includes(term),
    );
  });

  protected onTableFilter(value: string): void {
    this.tableFilter.set(value);
  }

  protected selectTable(key: string): void {
    if (key === this.selectedKey()) {
      return;
    }
    this.selectedKey.set(key);
    // A fresh table resets every row-level control.
    this.page.set(1);
    this.search.set('');
    this.orderBy.set(null);
    this.rows.set(null);
    this.loadRows();
  }

  private loadRows(): void {
    const key = this.selectedKey();
    if (!key) {
      return;
    }
    this.rowsLoading.set(true);
    this.rowsError.set(false);
    this.cdr.markForCheck();

    this.service
      .getRows(key, {
        page: this.page(),
        pageSize: PAGE_SIZE,
        search: this.search() || undefined,
        orderBy: this.orderBy() ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rows.set(res);
          // Adopt the ordering the server actually applied so the header
          // indicators never drift from the data.
          this.orderBy.set(res.order_by);
          this.rowsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.rowsLoading.set(false);
          this.rowsError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected onSearch(value: string): void {
    this.search.set(value.trim());
    this.page.set(1); // a new search invalidates the current page
    this.loadRows();
  }

  /** Toggle ascending/descending on a column, or sort a new column ascending. */
  protected sortBy(column: string): void {
    const current = this.orderBy();
    if (current === column) {
      this.orderBy.set(`-${column}`);
    } else {
      this.orderBy.set(column);
    }
    this.page.set(1);
    this.loadRows();
  }

  protected sortDirection(column: string): 'asc' | 'desc' | null {
    const current = this.orderBy();
    if (current === column) {
      return 'asc';
    }
    if (current === `-${column}`) {
      return 'desc';
    }
    return null;
  }

  protected nextPage(): void {
    if (this.rows()?.has_next) {
      this.page.update((p) => p + 1);
      this.loadRows();
    }
  }

  protected previousPage(): void {
    if (this.rows()?.has_previous) {
      this.page.update((p) => Math.max(1, p - 1));
      this.loadRows();
    }
  }

  protected retryRows(): void {
    this.loadRows();
  }

  // ── Export / import ───────────────────────────────────────────────────────

  /** Download the whole database as a zip, named by the server. */
  protected exportDatabase(): void {
    if (this.exporting()) {
      return;
    }
    this.exporting.set(true);
    this.exportError.set(null);
    this.cdr.markForCheck();

    this.service
      .exportDatabase()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.exporting.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: HttpResponse<Blob>) => {
          if (!res.body) {
            this.exportError.set('The export came back empty.');
            return;
          }
          const url = URL.createObjectURL(res.body);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = filenameFromDisposition(res.headers.get('Content-Disposition'));
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.exportError.set("Couldn't export the database. Please try again.");
        },
      });
  }

  protected onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.importFile.set(input.files?.[0] ?? null);
    this.importResult.set(null);
  }

  /**
   * Replace every table with the chosen archive's contents.
   *
   * Confirmed as destructive first: the import truncates and refills the whole
   * database, including the account the admin is signed in with. Where it is
   * allowed at all is decided server-side (production never, staging only when
   * unlocked), and a refusal lands in the result block.
   */
  protected importDatabase(fileInput: HTMLInputElement): void {
    const file = this.importFile();
    if (!file || this.importing()) {
      return;
    }

    const data: ConfirmDialogData = {
      title: 'Replace the database?',
      message:
        `Every table in this environment is emptied and refilled from "${file.name}". ` +
        'Existing records, including your own account, are replaced, and you will ' +
        'need to sign in again with an account from the imported data. Uploaded ' +
        'documents are not part of the archive.',
      confirmLabel: 'Replace database',
      destructive: true,
      confirm: () =>
        defer(() => {
          this.importing.set(true);
          this.importResult.set(null);
          this.cdr.markForCheck();
          return this.service.importDatabase(file);
        }).pipe(
          tap((result) => {
            this.importResult.set(result);
            if (result.status === 'ok') {
              this.importFile.set(null);
              fileInput.value = '';
              // Row counts and the selected table belong to the old data.
              this.selectedKey.set(null);
              this.rows.set(null);
              this.loadTables();
            }
          }),
          finalize(() => {
            this.importing.set(false);
            this.cdr.markForCheck();
          }),
        ),
      errorMessage: (err: unknown) =>
        (err as HttpErrorResponse)?.error?.detail ??
        "Couldn't import the database. Please try again.",
    };

    this.dialog.open(ConfirmDialogComponent, { width: '440px', data });
  }
}
